/**
 * Hook customizado para gerenciar cache de requisições à API
 * Utiliza React Query para cache inteligente
 */

import React from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiGet, buildPayload, generateCacheKey } from '../services/apiService';

/**
 * Presets de cache por tipo de dado
 */
export const CACHE_PRESETS = {
  // Dados estáticos (contratos, centros, equipes) - nunca considerados stale
  STATIC: {
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000 // 30 minutos no cache
  },
  // Dados diários (mudam frequentemente)
  DAILY: {
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000 // 5 minutos
  },
  // Dados padrão (performance, indicadores)
  DEFAULT: {
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000 // 10 minutos
  }
};

/**
 * Hook para buscar dados com cache
 * @param {string} endpoint - Endpoint da API (ex: '/percentual-turnos-diario')
 * @param {object} filters - Filtros { startDate, endDate, contrato, centro, equipe }
 * @param {object} options - Opções do React Query { enabled, staleTime, cacheTime, preset }
 */
export const useApiQuery = (endpoint, filters = {}, options = {}) => {
  const payload = buildPayload(filters);
  const queryKey = generateCacheKey(endpoint, payload);

  // Detectar preset baseado no endpoint ou usar preset fornecido
  let preset = options.preset;
  if (!preset) {
    // Auto-detectar preset baseado no endpoint
    if (endpoint.includes('/contratos') || endpoint.includes('/centros') || endpoint.includes('/equipes')) {
      preset = 'STATIC';
    } else if (endpoint.includes('-diario') || endpoint.includes('-dia')) {
      preset = 'DAILY';
    } else {
      preset = 'DEFAULT';
    }
  }

  const presetConfig = CACHE_PRESETS[preset] || CACHE_PRESETS.DEFAULT;

  const {
    enabled = true,
    staleTime = presetConfig.staleTime,
    gcTime = presetConfig.gcTime,
    refetchOnWindowFocus = false, // Não refetch ao focar a janela
    refetchOnMount = false, // NÃO refetch ao montar se já tiver cache (IMPORTANTE para cache funcionar)
    ...restOptions
  } = options;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Log para debug
      if (process.env.NODE_ENV === 'development') {
        console.log('[Cache] Fazendo requisição:', { endpoint, payload, queryKey: JSON.stringify(queryKey) });
      }
      return apiPost(endpoint, payload);
    },
    enabled: enabled && (filters.startDate || filters.endDate ? (!!filters.startDate && !!filters.endDate) : true),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount, // Alterado para false por padrão - só busca se não tiver cache
    ...restOptions
  });

  // Log para debug do status do cache - sempre chamado (não condicionalmente)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Cache] Status da query:', {
        endpoint,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isStale: query.isStale,
        dataUpdatedAt: query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toLocaleTimeString() : null,
        hasData: !!query.data
      });
    }
  }, [endpoint, query.isLoading, query.isFetching, query.isStale, query.data, query.dataUpdatedAt]);

  return query;
};

/**
 * Hook para requisições GET com cache
 */
export const useApiGet = (endpoint, params = {}, options = {}) => {
  const queryKey = [endpoint, params];
  
  const {
    staleTime = 5 * 60 * 1000,
    gcTime = 10 * 60 * 1000,
    refetchOnWindowFocus = false,
    ...restOptions
  } = options;

  return useQuery({
    queryKey,
    queryFn: () => apiGet(endpoint, params),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    ...restOptions
  });
};

/**
 * Hook para mutações (POST que alteram estado)
 * Permite invalidar cache após mutações
 */
export const useApiMutation = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ endpoint, payload }) => apiPost(endpoint, payload),
    onSuccess: (data, variables, context) => {
      // Invalidar queries relacionadas após mutação bem-sucedida
      if (options.invalidateQueries) {
        queryClient.invalidateQueries({ queryKey: options.invalidateQueries });
      }
      
      // Callback customizado se fornecido
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    }
  });
};

/**
 * Hook para buscar múltiplas queries em paralelo
 * Útil para dashboards que precisam de vários dados
 * 
 * Usa useQueries do React Query que suporta arrays dinâmicos
 */
export const useMultipleQueries = (queries) => {
  // Garantir que queries é um array válido - sempre chamar useQueries (mesmo com array vazio)
  const validQueries = Array.isArray(queries) && queries.length > 0 ? queries : [];
  
  // Usar useQueries do React Query que suporta arrays dinâmicos
  // IMPORTANTE: Sempre chamado, mesmo com array vazio (evita erro de hooks condicionais)
  const results = useQueries({
    queries: validQueries.map(({ endpoint, filters, options = {} }) => {
      const payload = buildPayload(filters);
      let queryKey = generateCacheKey(endpoint, payload);
      
      // Adiciona _key à queryKey se fornecido (força refetch)
      if (options._key !== undefined) {
        queryKey = [...queryKey, '_refresh', options._key];
      }
      
      // Detectar preset baseado no endpoint ou usar preset fornecido
      let preset = options.preset;
      if (!preset) {
        if (endpoint.includes('/contratos') || endpoint.includes('/centros') || endpoint.includes('/equipes')) {
          preset = 'STATIC';
        } else if (endpoint.includes('-diario') || endpoint.includes('-dia')) {
          preset = 'DAILY';
        } else {
          preset = 'DEFAULT';
        }
      }
      const presetConfig = CACHE_PRESETS[preset] || CACHE_PRESETS.DEFAULT;

      return {
        queryKey,
        queryFn: () => apiPost(endpoint, payload),
        enabled: options.enabled !== false && (filters.startDate || filters.endDate ? (!!filters.startDate && !!filters.endDate) : true),
        staleTime: options.staleTime !== undefined ? options.staleTime : presetConfig.staleTime,
        gcTime: options.gcTime !== undefined ? options.gcTime : presetConfig.gcTime,
        refetchOnWindowFocus: options.refetchOnWindowFocus !== undefined ? options.refetchOnWindowFocus : false,
        refetchOnMount: options.refetchOnMount !== undefined ? options.refetchOnMount : false
      };
    })
  });

  // Se não há queries, retornar valores padrão
  if (validQueries.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: false,
      errors: [],
      refetch: async () => []
    };
  }

  return {
    data: results.map(r => r.data),
    isLoading: results.some(r => r.isLoading),
    isError: results.some(r => r.isError),
    errors: results.map(r => r.error).filter(Boolean),
    refetch: () => Promise.all(results.map(r => r.refetch()))
  };
};

/**
 * Função utilitária para invalidar cache de queries específicas
 */
export const useInvalidateCache = () => {
  const queryClient = useQueryClient();

  return {
    invalidateEndpoint: (endpoint) => {
      queryClient.invalidateQueries({ queryKey: [endpoint] });
    },
    invalidateAll: () => {
      queryClient.invalidateQueries();
    },
    removeFromCache: (endpoint, payload) => {
      const queryKey = generateCacheKey(endpoint, payload);
      queryClient.removeQueries({ queryKey });
    }
  };
};

